import React, { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { CommissionsDialogProps } from '../types';

/** «298…, 202…» → список счетов; null — в списке есть не-номер счёта. */
function parseAccounts(raw: string): string[] | null {
  const list = raw.split(/[\s,;]+/).filter(Boolean);
  return list.every((a) => /^\d{10,32}$/.test(a)) ? list : null;
}

/**
 * Ставки комиссии по провайдерам. Ставка одна на провайдера и применяется ко
 * всей истории — история ставок не хранится (см. ADR 0019).
 */
export function CommissionsDialog({
  isDark,
  isOpen,
  onOpenChange,
  providers,
  onUpdate,
}: CommissionsDialogProps) {
  const [drafts, setDrafts] = useState<Record<string, { percent: string; from: string; accounts: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDrafts(
      Object.fromEntries(
        providers.map((p) => [
          p.id,
          {
            percent: String(p.commission_percent),
            from: p.commission_from || '',
            accounts: (p.sender_accounts ?? []).join(', '),
          },
        ]),
      ),
    );
  }, [isOpen, providers]);

  const inputCls = cn(
    'w-24',
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );

  const isDirty = (provider: CommissionsDialogProps['providers'][number]) => {
    const draft = drafts[provider.id];
    if (!draft) return false;
    return (
      draft.percent !== String(provider.commission_percent) ||
      draft.from !== (provider.commission_from || '') ||
      draft.accounts !== (provider.sender_accounts ?? []).join(', ')
    );
  };

  const handleSave = async (id: string) => {
    const draft = drafts[id];
    const value = parseFloat(draft?.percent ?? '');
    if (Number.isNaN(value) || value < 0 || value > 100) return;
    const senderAccounts = parseAccounts(draft.accounts);
    if (senderAccounts === null) return;
    setSavingId(id);
    try {
      await onUpdate(id, { commissionPercent: value, commissionFrom: draft.from || null, senderAccounts });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-xl', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            Комиссии провайдеров
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            Ставка применяется ко всей истории. Если по договору был льготный период —
            укажите дату, с которой провайдер начал удерживать комиссию: платежи раньше
            неё считаются без неё. Счета отправителя — с них провайдер зачисляет деньги;
            по ним Импорт выписки узнаёт его строки.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {providers.map((provider) => (
            <div
              key={provider.id}
              className={cn(
                'flex flex-wrap items-center justify-between gap-4 rounded-xl p-3',
                isDark ? 'bg-slate-800/50' : 'bg-slate-50',
              )}
            >
              <div>
                <p className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}>
                  {provider.name}
                </p>
                <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                  {provider.code}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={drafts[provider.id]?.percent ?? ''}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [provider.id]: { ...prev[provider.id], percent: e.target.value },
                      }))
                    }
                    className={inputCls}
                  />
                  <span className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>%</span>
                </div>

                <div className="flex items-center gap-2">
                  <Label className={cn('text-xs whitespace-nowrap', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    с
                  </Label>
                  <Input
                    type="date"
                    value={drafts[provider.id]?.from ?? ''}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [provider.id]: { ...prev[provider.id], from: e.target.value },
                      }))
                    }
                    className={cn(
                      'w-40',
                      isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
                    )}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Активен
                  </Label>
                  <Switch
                    checked={provider.is_active}
                    onCheckedChange={(checked) => onUpdate(provider.id, { isActive: checked })}
                  />
                </div>

                <Button
                  size="sm"
                  onClick={() => handleSave(provider.id)}
                  disabled={savingId === provider.id || !isDirty(provider)}
                  className="bg-[#C9A227] hover:bg-[#C9A227]/90"
                >
                  {savingId === provider.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Сохранить
                </Button>
              </div>

              {provider.currency === 'UZS' && (
                <div className="w-full space-y-1">
                  <Label className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Счета отправителя (через запятую)
                  </Label>
                  <Input
                    value={drafts[provider.id]?.accounts ?? ''}
                    placeholder="Например: 29896000804942713020"
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [provider.id]: { ...prev[provider.id], accounts: e.target.value },
                      }))
                    }
                    className={cn(
                      'font-mono text-xs',
                      isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
                      parseAccounts(drafts[provider.id]?.accounts ?? '') === null && 'border-red-500',
                    )}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
